"use client";

import { useLocale } from "next-intl";
import { useRouter } from "next/navigation";
import { useTransition } from "react";
import { useAction } from "next-safe-action/hooks";
import { setLocale } from "@/features/i18n/actions/set-locale";
import { cn } from "@/lib/utils";

export function LanguageSelector() {
  const locale = useLocale();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const { execute } = useAction(setLocale, {
    onSuccess: () => {
      startTransition(() => {
        router.refresh();
      });
    },
  });

  function handleSwitch(newLocale: "en" | "he") {
    if (newLocale === locale || isPending) return;
    execute({ locale: newLocale });
  }

  return (
    <div
      className={cn(
        "flex items-center h-8 rounded-full border border-border bg-background overflow-hidden",
        isPending && "opacity-60"
      )}
      role="group"
      aria-label="Language selector"
    >
      <button
        onClick={() => handleSwitch("en")}
        disabled={isPending}
        className={cn(
          "px-2.5 h-full text-xs font-medium transition-colors duration-150 select-none",
          locale === "en"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-accent"
        )}
        aria-pressed={locale === "en"}
        aria-label="Switch to English"
      >
        EN
      </button>
      <div className="w-px h-4 bg-border" />
      <button
        onClick={() => handleSwitch("he")}
        disabled={isPending}
        className={cn(
          "px-2.5 h-full text-xs font-medium transition-colors duration-150 select-none",
          locale === "he"
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-accent"
        )}
        aria-pressed={locale === "he"}
        aria-label="Switch to Hebrew"
        dir="rtl"
      >
        עב
      </button>
    </div>
  );
}
